// Integrations Hub (WP-11 / WhatChimp §1.8).
// Unified management for Email APIs (5), SMS APIs (7), and Autoresponders (5).
// Full CRUD with inline live test, verified badges, secret masking, and drawer editing.

import { useState, useEffect, useMemo, useRef } from 'react';
import { useI18n } from '../lib/i18n';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Card from '../components/ui/Card';
import EmptyState from '../components/ui/EmptyState';
import PillTabs from '../components/ui/PillTabs';
import Modal from '../components/ui/Modal';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';
import ProfileConfigDrawer from '../components/integrations/ProfileConfigDrawer';
import {
  type IntegrationKind,
  type IntegrationProfileDoc,
  PROVIDER_REGISTRY,
  integrationProfileList,
  integrationProfileDelete,
  integrationProfileTest,
} from '../lib/integrations';
import {
  PlusIcon,
  RefreshIcon,
  CheckIcon,
  AlertCircleIcon,
  ClockIcon,
} from '../components/icons';

type HubFilter = 'all' | 'email' | 'sms' | 'autoresponder';

function fmtDate(ms: number | null | undefined, neverText: string): string {
  if (!ms) return neverText;
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return neverText;
  }
}

export default function Integrations() {
  const { t } = useI18n();
  const toast = useToast();

  const [activeFilter, setActiveFilter] = useState<HubFilter>('all');
  const [profiles, setProfiles] = useState<IntegrationProfileDoc[]>([]);
  const [loading, setLoading] = useState(true);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerKind, setDrawerKind] = useState<IntegrationKind>('email');
  const [editingProfile, setEditingProfile] = useState<IntegrationProfileDoc | null>(null);

  // Inline testing state: profileId -> boolean
  const [testingId, setTestingId] = useState<string | null>(null);

  // Delete modal state
  const [profileToDelete, setProfileToDelete] = useState<IntegrationProfileDoc | null>(null);
  const [deleting, setDeleting] = useState(false);

  const mountedRef = useRef(true);

  // Load profiles
  const loadProfiles = async () => {
    if (mountedRef.current) setLoading(true);
    try {
      const listRes = await integrationProfileList({});
      if (!mountedRef.current) return;
      if (listRes.data.ok) {
        setProfiles(listRes.data.profiles || []);
      }
    } catch (err: any) {
      if (!mountedRef.current) return;
      toast.error(err.message || t('integrations.loadFailed'));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    loadProfiles();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const openCreateDrawer = (kind: IntegrationKind) => {
    setDrawerKind(kind);
    setEditingProfile(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (prof: IntegrationProfileDoc) => {
    setDrawerKind(prof.kind);
    setEditingProfile(prof);
    setDrawerOpen(true);
  };

  const handleProfileSaved = (savedDoc: IntegrationProfileDoc) => {
    setProfiles((prev) => {
      const idx = prev.findIndex((p) => p.id === savedDoc.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = savedDoc;
        return next;
      }
      return [savedDoc, ...prev];
    });
  };

  const handleTestInline = async (prof: IntegrationProfileDoc) => {
    setTestingId(prof.id);
    try {
      const testCallRes = await integrationProfileTest({ id: prof.id });
      if (testCallRes.data.verified) {
        toast.success(t('integrations.testSuccess'));
      } else {
        toast.error(testCallRes.data.lastError || t('integrations.testFailed'));
      }
      // Update local item
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === prof.id
            ? {
                ...p,
                verified: testCallRes.data.verified,
                lastTestAt: Date.now(),
                lastError: testCallRes.data.lastError ?? null,
              }
            : p
        )
      );
    } catch (err: any) {
      toast.error(err.message || t('integrations.testFailed'));
    } finally {
      setTestingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!profileToDelete) return;
    setDeleting(true);
    try {
      const delRes = await integrationProfileDelete({ id: profileToDelete.id });
      if (delRes.data.ok) {
        toast.success(t('integrations.profileDeleted'));
        setProfiles((prev) => prev.filter((p) => p.id !== profileToDelete.id));
        setProfileToDelete(null);
      }
    } catch (err: any) {
      toast.error(err.message || t('integrations.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const emailProfiles = useMemo(
    () => profiles.filter((p) => p.kind === 'email'),
    [profiles]
  );
  const smsProfiles = useMemo(
    () => profiles.filter((p) => p.kind === 'sms'),
    [profiles]
  );
  const autoresponderProfiles = useMemo(
    () => profiles.filter((p) => p.kind === 'autoresponder'),
    [profiles]
  );

  const filterTabs = [
    { id: 'all', label: t('integrations.tabAll'), count: profiles.length },
    { id: 'email', label: t('integrations.tabEmail'), count: emailProfiles.length },
    { id: 'sms', label: t('integrations.tabSms'), count: smsProfiles.length },
    { id: 'autoresponder', label: t('integrations.tabAutoresponder'), count: autoresponderProfiles.length },
  ];

  const renderSection = (
    title: string,
    subtitle: string,
    kind: IntegrationKind,
    items: IntegrationProfileDoc[],
    addLabel: string
  ) => {
    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-bold text-ink dark:text-slate-100 flex items-center gap-2">
              <span>{title}</span>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-ink-3 dark:bg-slate-800 dark:text-slate-400">
                {items.length}
              </span>
            </h2>
            <p className="text-xs text-ink-3 dark:text-slate-400">{subtitle}</p>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => openCreateDrawer(kind)}
            className="self-start sm:self-auto"
          >
            <PlusIcon size={16} />
            <span>{addLabel}</span>
          </Button>
        </div>

        {items.length === 0 ? (
          <EmptyState
            title={t('integrations.emptyTitle')}
            description={t('integrations.emptyDesc')}
            action={
              <Button variant="secondary" size="sm" onClick={() => openCreateDrawer(kind)}>
                <PlusIcon size={14} />
                <span>{addLabel}</span>
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((prof) => {
              const meta = PROVIDER_REGISTRY[prof.provider];
              const isTesting = testingId === prof.id;

              return (
                <Card key={prof.id} className="p-4 flex flex-col justify-between hover:shadow-md transition">
                  <div>
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="text-2xl shrink-0">{meta?.icon || '🔌'}</span>
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-ink dark:text-slate-100 truncate" title={prof.name}>
                            {prof.name}
                          </div>
                          <div className="text-xs text-ink-3 dark:text-slate-400">
                            {meta?.name || prof.provider}
                          </div>
                        </div>
                      </div>

                      {prof.verified ? (
                        <Badge variant="success" size="sm">
                          <CheckIcon size={10} className="me-1" />
                          <span>{t('integrations.verified')}</span>
                        </Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">
                          <AlertCircleIcon size={10} className="me-1" />
                          <span>{t('integrations.unverified')}</span>
                        </Badge>
                      )}
                    </div>

                    {/* Summary attributes */}
                    <div className="space-y-1.5 py-2.5 border-y border-line dark:border-slate-800 text-xs">
                      {prof.kind === 'email' && (
                        <div className="flex justify-between text-ink-2 dark:text-slate-300">
                          <span className="text-ink-3 dark:text-slate-400">{t('integrations.field.fromEmail')}:</span>
                          <span className="font-mono truncate max-w-[180px]">{prof.config.fromEmail || prof.config.host || '—'}</span>
                        </div>
                      )}
                      {prof.kind === 'sms' && (
                        <div className="flex justify-between text-ink-2 dark:text-slate-300">
                          <span className="text-ink-3 dark:text-slate-400">{t('integrations.field.sender')}:</span>
                          <span className="font-mono truncate max-w-[180px]">{prof.config.fromNumber || prof.config.fromSenderId || prof.config.url || '—'}</span>
                        </div>
                      )}
                      {prof.kind === 'autoresponder' && (
                        <div className="flex justify-between text-ink-2 dark:text-slate-300">
                          <span className="text-ink-3 dark:text-slate-400">{t('integrations.field.list')}:</span>
                          <span className="font-mono truncate max-w-[180px]">{prof.config.listId || prof.config.listUid || 'Default'}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-ink-3 dark:text-slate-400 text-[11px]">
                        <span className="flex items-center gap-1">
                          <ClockIcon size={12} />
                          <span>{t('integrations.lastTested')}:</span>
                        </span>
                        <span>{fmtDate(prof.lastTestAt, t('integrations.neverTested'))}</span>
                      </div>
                    </div>

                    {prof.lastError && !prof.verified && (
                      <p className="mt-2 text-[11px] text-rose-600 dark:text-rose-400 line-clamp-2">
                        {prof.lastError}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex items-center justify-between gap-2 pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTestInline(prof)}
                      disabled={isTesting}
                      className="text-xs"
                    >
                      {isTesting ? (
                        <>
                          <RefreshIcon size={12} className="animate-spin" />
                          <span>{t('common.testing')}</span>
                        </>
                      ) : (
                        t('integrations.test')
                      )}
                    </Button>

                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openEditDrawer(prof)}
                        className="text-xs"
                      >
                        {t('common.edit')}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setProfileToDelete(prof)}
                        className="text-xs"
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('integrations.pageTitle')}
        subtitle={t('integrations.pageSubtitle')}
      />

      {/* Tabs */}
      <PillTabs
        tabs={filterTabs}
        activeId={activeFilter}
        onChange={(id) => setActiveFilter(id as HubFilter)}
      />

      {loading ? (
        <div className="space-y-10">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="space-y-1">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-3 w-64" />
              </div>
              <Skeleton className="h-8 w-28 rounded-lg" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-card border border-line bg-surface p-4 shadow-sm space-y-3 dark:border-slate-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-5 w-16 rounded-full" />
                  </div>
                  <div className="border-y border-line py-3 space-y-2 dark:border-slate-800">
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-3/4" />
                  </div>
                  <div className="flex justify-between pt-1">
                    <Skeleton className="h-7 w-16 rounded-md" />
                    <div className="flex gap-1.5">
                      <Skeleton className="h-7 w-14 rounded-md" />
                      <Skeleton className="h-7 w-14 rounded-md" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-10">
          {(activeFilter === 'all' || activeFilter === 'email') &&
            renderSection(
              t('integrations.emailApi'),
              t('integrations.emailApiDesc'),
              'email',
              emailProfiles,
              t('integrations.addEmailProfile')
            )}

          {(activeFilter === 'all' || activeFilter === 'sms') &&
            renderSection(
              t('integrations.smsApi'),
              t('integrations.smsApiDesc'),
              'sms',
              smsProfiles,
              t('integrations.addSmsProfile')
            )}

          {(activeFilter === 'all' || activeFilter === 'autoresponder') &&
            renderSection(
              t('integrations.autoresponder'),
              t('integrations.autoresponderDesc'),
              'autoresponder',
              autoresponderProfiles,
              t('integrations.addAutoresponderProfile')
            )}
        </div>
      )}

      {/* Drawer */}
      <ProfileConfigDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        kind={drawerKind}
        profile={editingProfile}
        onSaved={handleProfileSaved}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={Boolean(profileToDelete)}
        onClose={() => setProfileToDelete(null)}
        title={t('integrations.deleteModalTitle')}
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-2 dark:text-slate-300">
            {t('integrations.deleteModalConfirm', { name: profileToDelete?.name || '' })}
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              onClick={() => setProfileToDelete(null)}
              disabled={deleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? t('common.deleting') : t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
